import { useEffect, useRef } from "react";
import { FriendlyCaptchaSDK } from "@friendlycaptcha/sdk";
import { FRIENDLY_CAPTCHA_KEY } from "config.js";

// One SDK instance is enough for the whole app; each widget gets its own handle.
const sdk = new FriendlyCaptchaSDK({ apiEndpoint: "global" });

const FriendlyCaptcha = ({ handleCaptchaValidation, registerReset }) => {
	const container = useRef();
	const widget = useRef();

	useEffect(() => {
		const element = container.current;
		if (widget.current || !element) return;

		// The response token is passed on rather than reduced to a boolean: the
		// server has to send it to Friendly Captcha to be verified. A boolean
		// proves nothing, since anything posted directly to the API never runs
		// this code at all.
		const onComplete = e => handleCaptchaValidation(e.detail.response || null);
		const onFailure = () => handleCaptchaValidation(null);

		element.addEventListener("frc:widget.complete", onComplete);
		element.addEventListener("frc:widget.error", onFailure);
		element.addEventListener("frc:widget.expire", onFailure);

		widget.current = sdk.createWidget({
			element: element,
			sitekey: FRIENDLY_CAPTCHA_KEY
		});

		// A response can only be verified once, so the form needs a way to ask for
		// a fresh challenge after a submission.
		if (registerReset) {
			registerReset(() => {
				handleCaptchaValidation(null);
				if (widget.current) widget.current.reset();
			});
		}

		return () => {
			element.removeEventListener("frc:widget.complete", onComplete);
			element.removeEventListener("frc:widget.error", onFailure);
			element.removeEventListener("frc:widget.expire", onFailure);
			if (widget.current) {
				widget.current.destroy();
				widget.current = undefined;
			}
		};
		// The widget is created once on mount and torn down on unmount; the props
		// are read at that moment and re-running would destroy a solved widget.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [container]);

	return <div ref={container} className="frc-captcha" />;
};

export default FriendlyCaptcha;
